/**
 * Returns a pair of export handler functions (Excel + PDF) for GHG pages.
 * Eliminates the duplicated try/catch/finally export pattern that was shared
 * between GHGOverviewPage and CompanyGHGPage.
 */

import { exportGHGToExcel, exportGHGToPdf } from '../../services/ghgReportingApi';
import { buildDateRangeOptions } from './ghgStatsUtils';

export interface GhgExportHandlerOptions {
  filterMode: 'year' | 'dateRange';
  fromDate: Date | null;
  toDate: Date | null;
  selectedYear: number;
  setExporting: (exporting: boolean) => void;
  setError: (msg: string | null) => void;
  /** Optional – included in export options when present. */
  companyId?: number | null;
  /** Optional – included in export options when present (overview page only). */
  statusFilter?: any;
}

export interface GhgExportHandlers {
  handleExcelExport: () => Promise<void>;
  handlePdfExport: () => Promise<void>;
}

export function useGhgExportHandlers({
  filterMode,
  fromDate,
  toDate,
  selectedYear,
  setExporting,
  setError,
  companyId,
  statusFilter,
}: GhgExportHandlerOptions): GhgExportHandlers {
  const buildOptions = () => {
    const options: any = { ...buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear) };
    if (companyId) options.companyId = companyId;
    if (statusFilter !== undefined) options.statusFilter = statusFilter;
    return options;
  };

  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const downloadUrl = await exportGHGToExcel(buildOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handlePdfExport = async () => {
    setExporting(true);
    try {
      const downloadUrl = await exportGHGToPdf(buildOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export to PDF');
    } finally {
      setExporting(false);
    }
  };

  return { handleExcelExport, handlePdfExport };
}
